import React from 'react'
import styled from 'styled-components'

import type {
  ChecklistType,
  ChecklistEntries,
  PatchChecklistItem,
  PatchChecklistMutation,
} from '@/modules/checklists'
import { Button, Checkbox } from '@/components_deprecated'
import { PATCH_CHECKLIST_MUTATION } from '../mutations'
import { useMutation } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import { USER_CHECKLISTS } from '../queries'
import { useUser } from '@/utils/hooks'
import { BOOTSTRAP_QUERY } from '@/modules/authentication'

const HorizontalFlex = styled.div`
  display: flex;
  flex-direction: row;
  p {
    margin-left: 1rem;
  }
`

const VerticalFlex = styled.div`
  display: flex;
  flex-direction: column;
  ${HorizontalFlex} {
    &:not(:last-child) {
      margin-bottom: 0.5rem;
    }

    &:first-child {
      margin-top: 0.25rem;
    }
  }
  margin-bottom: 1rem;
`

const ChecklistWrapper = styled.div`
  margin-bottom: 1rem;
  width: 100%;
`

export interface ChecklistProps {
  checklist: ChecklistType | null
  mandatory: boolean
  viewOnly?: boolean
  onChange: (
    checklist: ChecklistType,
    checklistItem: PatchChecklistItem,
    value: ChecklistEntries
  ) => void
  onSubmit: () => void
  removeChecklistFromForm: (id: string) => void
}

export const Checklist: React.JSX.CFC<ChecklistProps> = ({
  checklist,
  mandatory,
  viewOnly,
  onChange,
  onSubmit,
  removeChecklistFromForm,
}) => {
  const translations = useTranslate({
    submit: 'common.submit',
  })

  const user = useUser()
  const [submitChecklist] = useMutation<PatchChecklistMutation>(
    PATCH_CHECKLIST_MUTATION,
    {
      refetchQueries: [
        {
          query: USER_CHECKLISTS,
          variables: { user: user.id, mandatoryOnly: mandatory },
        },
        { query: BOOTSTRAP_QUERY },
      ],
    }
  )

  if (checklist === null) {
    return null
  }

  if (!checklist.active && !viewOnly) return null

  function handleSubmit() {
    onSubmit()
    void submitChecklist({
      variables: {
        id: checklist?.id,
        input: {
          active: false,
        },
      },
    })
    checklist && removeChecklistFromForm(checklist.id)
  }

  return (
    <ChecklistWrapper>
      <h3>{checklist.title}</h3>

      <VerticalFlex>
        {Object.entries(checklist.entries).map(([key, entry]) => (
          <HorizontalFlex key={key}>
            <Checkbox
              checked={entry.done}
              disabled={viewOnly}
              onChange={done =>
                !viewOnly &&
                onChange(
                  checklist,
                  { id: entry.id, done },
                  Object.assign(checklist.entries, {
                    [entry.id]: {
                      id: entry.id,
                      text: entry.text,
                      done,
                    },
                  })
                )
              }
            />
            <p>{entry.text}</p>
          </HorizontalFlex>
        ))}
      </VerticalFlex>

      {!viewOnly && (
        <Button
          onClick={() => handleSubmit()}
          padding="0.25rem 0.75rem !important"
        >
          {translations.submit}
        </Button>
      )}
    </ChecklistWrapper>
  )
}
